import { Stack, Text, useTheme } from "@bookingcom/bui-react";
import { SearchboxController, SearchBoxVerticalDefault } from "@bookingcom/flights-searchbox";
import { t } from "@bookingcom/lingojs-core";
import { useI18n } from "@bookingcom/lingojs-react";
import FrequentlyAskedQuestions from "../components/elements/FrequentlyAskedQuestions/index.js";
import UspBlock from "../components/elements/UspBlock/index.js";
import React, { Profiler, useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { trackWithDefaultStage } from "@flights/et-universal";
import { trackMetaLandingsCustomGoalsV2 } from "../utils/experiments/track-meta-landings-v2.js";
import { Search, SearchOutboundMobile } from "../app/routes.js";
import AppShell from "../components/elements/AppShell/index.js";
import useClientMetrics from "../hooks/useClientMetrics.js";
import useGlobalContext from "../hooks/useGlobalContext.js";
import useLocaleContext from "../hooks/useLocaleContext.js";
import useSearchbox from "../hooks/useSearchbox.js";
import useTrackPageLoad from "../hooks/useTrackPageLoad.js";
import NoJS from "./NoJS.js";
import StaticContext from "../app/StaticContext/StaticContext.js";
import Frame from "../components/elements/Frame/index.js";
import { RemoteAppleCardIndex, RemoteGeniusLaunchSheet, RemoteGeniusLoyalty, RemoteRewardsLandSheet } from "../components/remote/index.js";
import PopularFlights from "../components/elements/PopularFlights/index.js";
import useLandingPageEvents from "../hooks/c360/useLandingPageEvents.js";
import { FlightsInterlinking } from "../components/elements/FlightsInterlinking/index.js";
import { goal } from "@flights/et-universal";
import { useIsMapViewExp } from "../components/elements/FlyAnywhere/hooks/useIsMapViewExp.js";
import flights_web_cat_fly_anywhere_map_view_mdot from "../utils/experiments/cat/flights_web_cat_fly_anywhere_map_view_mdot.js";
import useFareCalendarEligible from "../hooks/useFareCalendarEligible.js";
import FlyAnywhereCountriesCarousel from "../components/elements/FlyAnywhere/FlyAnywhereCountriesCarousel.js";
import HomepageOffers from "../components/elements/HomepageOffers/index.js";
import { useSplitBoundsCondition } from "../utils/experiments/funnel/flights_split_bounds_us.js";
import FlyAnywhereMapBanner from "../components/elements/FlyAnywhere/FlyAnywhereMap/FlyAnywhereMapBanner.js";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { useSpringDeal } from "../hooks/useSpringDeal.js";
import { useSummerDeal } from "../hooks/useSummerDeal.js";
import { MiddleEastBanner } from "../components/elements/MiddleEastBanner/MiddleEastBanner.js";
const Home = ({ getInitialState }) => {
    const staticContext = useContext(StaticContext);
    const theme = useTheme();
    const i18n = useI18n();
    const trackContextEventV2 = useTrackPageLoad("index");
    const { userAgent: { isWebview }, currencyCode } = useGlobalContext();
    const searchBoxProps = useSearchbox();
    const { isRTL } = useLocaleContext();
    const { trackRenderTime } = useClientMetrics();
    const trackLandingPageEvents = useLandingPageEvents();
    const { isSplitBounds } = useSplitBoundsCondition();
    const { isSpringDealExp } = useSpringDeal();
    const { isSummerDealExp } = useSummerDeal();
    const { features } = useGlobalContext();
    const isFareCalendarEligible = useFareCalendarEligible();
    const isMapViewExp = useIsMapViewExp();
    if (getInitialState && staticContext.set) {
        staticContext.set({ getInitialState: getInitialState() });
    }
    useEffect(() => {
        Search.preload();
        if (isSplitBounds) {
            SearchOutboundMobile.preload();
        }
        flightsSessionStore.set("index_page_load_time", Date.now());
        trackContextEventV2();
        goal("flights_home_page_view");
        goal("tbu_vertical_home_page_view");
        trackLandingPageEvents("page_view", "homepage");
        trackMetaLandingsCustomGoalsV2(2, "mdot");
        flights_web_cat_fly_anywhere_map_view_mdot.stages.main();
    }, []);
    return (React.createElement(Profiler, { id: "screen", onRender: trackRenderTime },
        React.createElement(PageHeader, null),
        React.createElement(AppShell, { noPadding: true },
            React.createElement(Frame, { alignItems: "center", direction: "column", justifyContent: "center", grow: 1 },
                React.createElement(Frame, { attributes: {
                        style: {
                            backgroundColor: isWebview
                                ? theme.colors.color_background_base
                                : theme.colors.color_background_disabled_alt,
                            width: "100%"
                        }
                    }, pr: 4, pl: 4 },
                    React.createElement(NoJS, null),
                    React.createElement(Frame, { mt: 4, mb: 4 },
                        React.createElement(Stack, { gap: 2 },
                            React.createElement(Text, { variant: "headline_1", tagName: "h1" }, i18n.trans(t("flights_index_header_compare_book_ease"))),
                            !isWebview && (React.createElement("span", { style: { color: theme.colors.color_foreground_alt } },
                                React.createElement(Text, { variant: "body_1" }, i18n.trans(t("flights_index_header_discover_next_dream"))))))),
                    React.createElement(Frame, { mb: 6 },
                        React.createElement(SearchboxController, { i18n: i18n, ...searchBoxProps, isRTL: isRTL, onSearch: (searchParams) => {
                                trackLandingPageEvents("click", "searchbox");
                                searchBoxProps.onSearch(searchParams);
                            }, enabledCountriesAndAnywhere: true, allowEmptyDestination: true, isSpringDealExp: isSpringDealExp, isSummerDealExp: isSummerDealExp, isSearchAnyCityEnabled: true, fareCalendarConfig: {
                                isFareCalendarMobileEnabled: isFareCalendarEligible &&
                                    (!!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1) ||
                                        !!trackWithDefaultStage("flights_web_cat_fare_calendar_data_collection", 1)),
                                isFareCalendarDisplayEnabled: isFareCalendarEligible && !!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1),
                                currencyCode
                            } }, () => React.createElement(SearchBoxVerticalDefault, null)))),
                features.FLIGHTS_WEB_PB_CS_HIGH_VOLUMES_BANNER ? (React.createElement(Frame, { attributes: { style: { width: "100%" } }, mt: 4, p: 4 },
                    React.createElement(MiddleEastBanner, null))) : undefined,
                React.createElement(HomepageOffers, { isMobile: true }),
                isMapViewExp && (React.createElement(Frame, { attributes: { style: { width: "100%" } }, mt: 4, p: 4 },
                    React.createElement(FlyAnywhereMapBanner, null))),
                React.createElement(Frame, { attributes: { style: { width: "100%" } }, mt: 4, p: 4 },
                    React.createElement(FlyAnywhereCountriesCarousel, null)),
                React.createElement(PopularFlights, { isMobile: true }),
                React.createElement(RemoteAppleCardIndex, null),
                React.createElement(RemoteGeniusLoyalty, null),
                React.createElement(Frame, { attributes: { style: { width: "100%" } }, mb: 6 },
                    React.createElement(UspBlock, null)),
                React.createElement(FlightsInterlinking, null),
                React.createElement(Frame, { attributes: { style: { width: "100%" } }, grow: 1, p: 4 },
                    React.createElement(FrequentlyAskedQuestions, null)))),
        React.createElement(RemoteGeniusLaunchSheet, null),
        React.createElement(RemoteRewardsLandSheet, null)));
};
function PageHeader(props) {
    const i18n = useI18n();
    return (React.createElement(Helmet, null,
        React.createElement("title", null, i18n.trans(t("mlx_flights_index_meta_title", {
            variables: { b_companyname: "Booking.com" }
        }))),
        React.createElement("meta", { name: "description", content: i18n.trans(t("mlx_flights_index_meta_description")) }),
        React.createElement("meta", { property: "webview:header", content: "regular" }),
        React.createElement("meta", { property: "webview:title", content: "" }),
        props.children));
}
export default Home;
export { PageHeader };
